import { useEffect, useState } from "react"
import { Phone, PhoneOff, Video } from "lucide-react"
import {
  selectShouldPlaySound,
  useSettingsStore,
} from "@/features/settings"
import { callRingtoneAsset } from "@/features/settings/constants/settings.constants"
import { AvatarPhoto } from "./avatar-photo"

interface IncomingCallOverlayProps {
  callerName: string
  callerImage?: string
  /** The caller's contactPresenceId, when known (the real `GlobalCallLayer`
   *  surface has it). Looked up against the live directory so a changed
   *  picture shows instead of the copy the ring carried. */
  contactId?: string
  isVideo?: boolean
  onAccept: () => void
  onDecline: () => void
}

function Avatar({ name, imageUrl, contactId }: { name: string; imageUrl?: string; contactId?: string }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/30 text-primary", "bg-violet-500/30 text-violet-300", "bg-emerald-500/30 text-emerald-300", "bg-amber-500/30 text-amber-300"]
  const c = colors[name.charCodeAt(0) % colors.length]
  return (
    <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold overflow-hidden shrink-0 ${c}`}>
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={initials} contactId={contactId} />
    </div>
  )
}

export function IncomingCallOverlay({ callerName, callerImage, contactId, isVideo = false, onAccept, onDecline }: IncomingCallOverlayProps) {
  const [opacity, setOpacity] = useState(0)

  // Subscribe to the settings store so toggling "Incoming call sounds" (or
  // "Mute all" / DND) mid-ring stops the audio instantly.
  const callSoundAllowed = useSettingsStore(selectShouldPlaySound("call"))
  // The ringtone the user picked, not a fixed file. Choosing one used to save
  // the choice and raise a toast confirming it, while this played the same
  // hardcoded mp3 whatever had been chosen.
  const ringtone = useSettingsStore((s) => s.sounds.callRingtone)

  useEffect(() => {
    const t = requestAnimationFrame(() => setOpacity(1))
    return () => cancelAnimationFrame(t)
  }, [])

  useEffect(() => {
    if (!callSoundAllowed) return
    let audio: HTMLAudioElement | null = null
    try {
      audio = new Audio(callRingtoneAsset(ringtone))
      audio.loop = true
      audio.volume = 0.7
      audio.play().catch(() => {})
    } catch {}
    return () => {
      if (audio) { audio.pause(); audio.currentTime = 0 }
    }
    // Changing the ringtone mid-ring restarts it on the new sound, which is
    // also what lets the picker be heard while a call is up.
  }, [callSoundAllowed, ringtone])

  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-start sm:justify-end pointer-events-none"
      style={{ opacity, transition: "opacity 300ms ease" }}
    >
      <div className="pointer-events-auto m-4 sm:m-6 bg-zinc-900/96 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden w-full max-w-sm">
        {/* Gradient top accent */}
        <div className="h-1 bg-gradient-to-r from-primary via-violet-500 to-primary" />

        <div className="px-5 pt-4 pb-5">
          {/* Header */}
          <p className="text-[11px] text-white/50 font-medium mb-3 uppercase tracking-wider">
            {isVideo ? "Incoming video call" : "Incoming call"}
          </p>

          {/* Caller info */}
          <div className="flex items-center gap-4 mb-5">
            <div className="relative">
              <Avatar name={callerName} imageUrl={callerImage} contactId={contactId} />
              {/* Animated ring */}
              <span className="absolute inset-0 rounded-full animate-ping bg-primary/20 pointer-events-none" />
            </div>
            <div>
              <p className="text-lg font-semibold text-white leading-tight">{callerName}</p>
              <div className="flex items-center gap-1.5 mt-1">
                {isVideo ? <Video className="w-3.5 h-3.5 text-white/50" /> : <Phone className="w-3.5 h-3.5 text-white/50" />}
                <span className="text-xs text-white/50">via Inmeet</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={onDecline}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-destructive/80 hover:bg-destructive text-white font-medium text-sm transition-all active:scale-95"
            >
              <PhoneOff className="w-4 h-4" />
              Decline
            </button>
            <button
              onClick={onAccept}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-green-500 hover:bg-green-400 text-white font-medium text-sm transition-all active:scale-95"
            >
              {isVideo ? <Video className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
